import { useAuth0 } from "@auth0/auth0-react";
import { FC, useState, ReactNode, createContext, useEffect, Dispatch } from "react";
import { createCheckoutSession } from "src/api/createCheckoutSession";
import { createClient } from "src/api/createClients";
import { getClientData } from "src/api/getClientData";
import { getClientReceipts } from "src/api/getClientReceipts";
import { getMonthlyBill } from "src/api/getMonthlyBill";
import { getRemainingRequests } from "src/api/getRemainingCredits";

import { getStatsData } from "src/api/getStatsData";
import { showErrorToast } from "src/components/AlertToastify/AlertToastify";
import { ClientInfoInterface } from "src/types/ClientInfoInterface";
import { MonthlyBillsInterface } from "src/types/MonthlyBill";
import { RemainingRequests } from "src/types/RemainingCreditsInterface";

import { StatsInterface } from "src/types/StatsInterface";
import { UpdatespendLimitInterface } from "src/types/UpdateSpentLimit";

interface Receipt {
  receipt_url: string;
  amount: number;
  date: string;
  invoice_id: string;
  status: string;
}

type SidebarContextType = {
  sidebarToggle: boolean;
  toggleSidebar: () => void;
  closeSidebar: () => void;
  clientInfo: ClientInfoInterface | null;
  remainingRequests: RemainingRequests | null;
  isDataLoaded: boolean;
  stats: StatsInterface | null;
  monthlyBill: MonthlyBillsInterface | null;
  // updatespendLimit: UpdatespendLimitInterface | null;
  setIsDataLoaded: Dispatch<boolean>;
  fetchRemainingRequests: () => Promise<void>;
  showUpgradeBanner: boolean;
  setShowUpgradeBanner: (value: boolean) => void;
  handleUpgrade: () => Promise<void>;
  showIncreaseLimitsBanner: boolean;
  setShowIncreaseLimitsBanner: (value: boolean) => void;
  fetchStats: (month?: number, year?: number) => Promise<void>;
  selectedMonth: number;
  selectedYear: number;
  handlePreviousMonth: () => void;
  handleNextMonth: () => void;
  receipts: Receipt[];
  loadingReceipts: boolean;
};

export const SidebarContext = createContext<SidebarContextType>({} as SidebarContextType);

type SidebarProviderProps = {
  children: ReactNode;
};

export const SidebarProvider: FC<SidebarProviderProps> = ({ children }) => {
  const [sidebarToggle, setSidebarToggle] = useState(false);

  //Satet to store client Id and client Secret from auth0
  const [clientInfo, setClientInfo] = useState<ClientInfoInterface | null>(null);
  //State to store remaining requests data of the client
  const [remainingRequests, setRemainingRequests] = useState<RemainingRequests | null>(null);
  //Sate for store stats data
  const [stats, setStats] = useState<StatsInterface | null>(null);

  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  //Sate for store client monthly Bill data
  const [monthlyBill, setMonthlyBill] = useState<MonthlyBillsInterface | null>(null);

  const [showUpgradeBanner, setShowUpgradeBanner] = useState(false);
  const [showIncreaseLimitsBanner, setShowIncreaseLimitsBanner] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  const { user, getAccessTokenSilently } = useAuth0();

  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loadingReceipts, setLoadingReceipts] = useState<boolean>(true);
  const [hasFetchedReceipts, setHasFetchedReceipts] = useState<boolean>(false);

  //Get data from Auth0 user
  const userData = user;
  const userAuthEmail = userData?.email;
  const userAuthId = userData?.sub;

  //Funciton to get accessAuthToken from Auth0, token from API, and create/get client data
  const fetchAuthToken = async () => {
    try {
      const accessAuthToken = await getAccessTokenSilently();

      //Create clientDataAuth with required data
      const clientDataAuth = {
        email: userAuthEmail,
        authId: userAuthId,
      };

      const clientData = await createClient(accessAuthToken, clientDataAuth);
      const getClientInfo = await getClientData(accessAuthToken, clientDataAuth);
      //Update clientInfo state to render client information on the dashboard (client id and client secret)
      setClientInfo({
        clientId: getClientInfo.clientId,
        clientSecret: getClientInfo.clientSecret,
      });

      //Create clientDataAuth with required data
    } catch (error) {
      showErrorToast(" Request failed. Please try again.");
    }
  };

  useEffect(() => {
    if (user) {
      fetchAuthToken();
    }
  }, [user]);

  //Function to get remaining requests data
  const fetchRemainingRequests = async () => {
    try {
      const accessAuthToken = await getAccessTokenSilently();

      if (clientInfo?.clientId) {
        const remainingRequestsData = await getRemainingRequests(accessAuthToken, clientInfo.clientId);
        //Update remaining requests state to render in the dashboard data about credtis
        setRemainingRequests(remainingRequestsData);
        setIsDataLoaded(true);
      }
    } catch (error) {
      console.error("Failed to get client remaining requests data", error);
    }
  };

  useEffect(() => {
    if (clientInfo) {
      fetchRemainingRequests();
    }
  }, [clientInfo]);

  //Function to get stats data
  const fetchStats = async (month?: number, year?: number) => {
    try {
      const accessAuthToken = await getAccessTokenSilently();

      const statsData = await getStatsData(accessAuthToken, clientInfo.clientId, month, year);
      //Update stats state to render in the dashboard data about clients stats
      setStats(statsData);
      setIsDataLoaded(true);
    } catch (error) {
      console.error("Failed to get client stats data", error);
    }
  };

  // Function to change previous month
  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear((prev) => prev - 1);
    } else {
      setSelectedMonth((prev) => prev - 1);
    }
  };
  // Function to change next month
  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear((prev) => prev + 1);
    } else {
      setSelectedMonth((prev) => prev + 1);
    }
  };
  useEffect(() => {
    if (clientInfo) {
      fetchStats(selectedMonth, selectedYear);
    }
  }, [clientInfo, selectedMonth, selectedYear]);

  //Funciton to get client monthly Bill data
  const fetchMonthlyBill = async () => {
    try {
      const accessAuthToken = await getAccessTokenSilently();

      if (clientInfo?.clientId) {
        const monthlyBillData = await getMonthlyBill(accessAuthToken, clientInfo.clientId);
        //Update updatespendLimit state to render client update spend Limit data on the dashboard
        setMonthlyBill(monthlyBillData);
        setIsDataLoaded(true);
      }
    } catch (error) {
      showErrorToast(" Request failed. Please try again.");
    }
  };

  useEffect(() => {
    if (clientInfo) {
      fetchMonthlyBill();
    }
  }, [clientInfo]);

  //Function to handle  stripe data request in button Upgrade
  const handleUpgrade = async () => {
    try {
      const accessAuthToken = await getAccessTokenSilently();

      if (!clientInfo?.clientId) {
        throw new Error("Client ID is not available");
      }

      const response = await createCheckoutSession(accessAuthToken, clientInfo.clientId);

      if (response.checkout_url) {
        //Redirect user to checkout stripe page
        window.location.href = response.checkout_url;
      } else {
        console.error("No checkout_url in response");
      }
    } catch (error) {
      console.error("Error creating checkout session", error);
    }
  };

  const fetchReceipts = async () => {
    try {
      setLoadingReceipts(true);
      const accessToken = await getAccessTokenSilently();
      if (clientInfo?.clientId) {
        const data = await getClientReceipts(accessToken, clientInfo.clientId);
        setReceipts(data.receipts);
      }
    } catch (error) {
      console.error("Error fetching receipts:", error);
    } finally {
      setLoadingReceipts(false);
    }
  };

  useEffect(() => {
    fetchReceipts();
  }, [clientInfo, getAccessTokenSilently]);

  const toggleSidebar = () => {
    setSidebarToggle(!sidebarToggle);
  };
  const closeSidebar = () => {
    setSidebarToggle(false);
  };

  // console.log("monthlyBillData", monthlyBill);

  return (
    <SidebarContext.Provider
      value={{
        sidebarToggle,
        toggleSidebar,
        closeSidebar,
        clientInfo,
        remainingRequests,
        isDataLoaded,
        stats,
        monthlyBill,
        setIsDataLoaded,
        fetchRemainingRequests,
        showUpgradeBanner,
        setShowUpgradeBanner,
        handleUpgrade,
        showIncreaseLimitsBanner,
        setShowIncreaseLimitsBanner,
        fetchStats,
        selectedMonth,
        selectedYear,
        handlePreviousMonth,
        handleNextMonth,
        receipts,
        loadingReceipts,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};
